import type { AutomationNeedingAttentionResolveBody } from 'app/pages/HumanInTheLoopPage/apiService/humanInTheLoopApiService';

export const NURTURING_AUTOMATION_EVENT_PREFIX = 'nurturing_test_automation_';
export const NURTURING_AUTOMATION_EVENT_COMPLETED =
  'nurturing_test_automation_completed';
export const NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED =
  'nurturing_test_automation_request_received';
export const NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS =
  'nurturing_test_automation_dealer_progress';
export const NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS =
  'nurturing_test_automation_lead_progress';

export type TestNurturingJourneyResult = 'success' | 'failure';

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Returns "success" when sent count matches expected count, else "failure".
 */
export function getTestNurturingJourneyResultStatus(
  messagesSentCount: unknown,
  expectedSentCount: unknown,
): TestNurturingJourneyResult {
  const sent = toFiniteNumber(messagesSentCount);
  const expected = toFiniteNumber(expectedSentCount);
  if (sent == null || expected == null) return 'failure';
  return sent === expected ? 'success' : 'failure';
}

export type NurturingAutomationSocketMessage = {
  eventName: string;
  data: unknown;
  timestamp: number;
};

export function isNurturingAutomationHttpSuccess(statusCode?: number): boolean {
  if (statusCode == null) return true;
  return statusCode >= 200 && statusCode < 300;
}

export function getNurturingAutomationSocketMessage(
  payload: unknown,
): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  const p = payload as Record<string, unknown>;
  const nested = p.message;
  if (nested && typeof nested === 'object') {
    return nested as Record<string, unknown>;
  }
  return p;
}

export function getNurturingAutomationJobId(payload: unknown): string {
  const msg = getNurturingAutomationSocketMessage(payload);
  let value = msg.job_id ?? msg.jobId;
  if (value == null && payload && typeof payload === 'object') {
    const root = payload as Record<string, unknown>;
    value = root.job_id ?? root.jobId;
  }
  if (value == null) return '';
  return String(value).trim();
}

export function getNurturingAutomationLeadCounts(payload: unknown): {
  current: number | null;
  total: number | null;
} {
  const msg = getNurturingAutomationSocketMessage(payload);
  const totalRaw = msg.total_leads ?? msg.total ?? msg.lead_count;
  const currentRaw = msg.completed_leads ?? msg.current;
  const total =
    typeof totalRaw === 'number' && Number.isFinite(totalRaw)
      ? Math.max(0, totalRaw)
      : null;
  const current =
    typeof currentRaw === 'number' && Number.isFinite(currentRaw)
      ? Math.max(0, currentRaw)
      : null;
  return { current, total };
}

export function extractJobIdsFromResolveBody(
  body: AutomationNeedingAttentionResolveBody | null | undefined,
): string[] {
  if (!body || typeof body !== 'object') return [];
  const ids = new Set<string>();

  const reruns = body.reruns;
  if (Array.isArray(reruns)) {
    for (const rerun of reruns) {
      const testId = rerun?.test_automation_id?.trim();
      if (testId) ids.add(testId);
      const jobTopic = rerun?.websocket?.subscribe_job_topic?.trim();
      if (jobTopic) ids.add(jobTopic);
    }
  }

  const topJobTopic = body.websocket?.subscribe_job_topic?.trim();
  if (topJobTopic) ids.add(topJobTopic);

  return Array.from(ids);
}

export function getResolveProgressTotal(
  body: AutomationNeedingAttentionResolveBody | null | undefined,
  fallbackTotal: number,
): number {
  if (!body || typeof body !== 'object') {
    return Math.max(1, fallbackTotal);
  }

  let sum = 0;
  const reruns = body.reruns;
  if (Array.isArray(reruns)) {
    for (const rerun of reruns) {
      if (Array.isArray(rerun.items) && rerun.items.length > 0) {
        sum += rerun.items.length;
      } else if (Array.isArray(rerun.task_ids) && rerun.task_ids.length > 0) {
        sum += rerun.task_ids.length;
      }
    }
  }

  if (sum > 0) return Math.max(1, sum);

  const rescheduled = body.rescheduled_count;
  if (typeof rescheduled === 'number' && Number.isFinite(rescheduled) && rescheduled > 0) {
    return Math.max(1, rescheduled);
  }

  return Math.max(1, fallbackTotal);
}
